/**
 * Клиентский executor для отложенных серверных команд.
 * Забирает команды через claim, выполняет, подтверждает через ack.
 * Вызывается один раз при инициализации SDK после загрузки страницы
 */

import log from "loglevel";
import OpenApiClient from "@app/Api/OpenApiClient";
import type {
	FxIdWebFeaturesClientCommandsClaimClaimedCommand,
	FxIdWebFeaturesClientCommandsAckAckItem
} from "@app/Api/gen";

const ACK_BATCH_SIZE = 10;

/**
 * Забирает и выполняет pending команды для текущего аккаунта.
 * Ack отправляется только после успешного выполнения каждой команды.
 * Если выполнение упало — lease истечёт и команда будет доступна при следующем claim
 */
export async function processClientCommands(): Promise<void> {
	try {
		const data = await OpenApiClient.ClientCommands.fxIdWebFeaturesClientCommandsClaimEndpoint();
		if (data.Commands.length === 0) return;

		const acked: FxIdWebFeaturesClientCommandsAckAckItem[] = [];

		for (const cmd of data.Commands) {
			try {
				executeCommand(cmd);
				acked.push({ Id: cmd.Id, LeaseToken: cmd.LeaseToken });
			} catch (e) {
				log.error(`Failed to execute client command ${cmd.Id}:`, e);
				// Не добавляем в acked — lease истечёт → retry при следующем claim
			}
		}

		// Отправляем ack батчами по 10 (лимит сервера)
		for (let i = 0; i < acked.length; i += ACK_BATCH_SIZE) {
			const batch = acked.slice(i, i + ACK_BATCH_SIZE);
			await OpenApiClient.ClientCommands.fxIdWebFeaturesClientCommandsAckEndpoint({
				Items: batch
			});
		}
	} catch (e) {
		log.error("Client commands processing error:", e);
	}
}

/**
 * Выполняет одну клиентскую команду в зависимости от типа
 */
function executeCommand(cmd: FxIdWebFeaturesClientCommandsClaimClaimedCommand): void {
	switch (cmd.CommandType) {
		case "gtag_event":
			window.dataLayer?.push(cmd.CommandData as Record<string, unknown>);
			break;
		default:
			log.warn(`Unknown client command type: ${cmd.CommandType}`);
	}
}
